import { gql, Observable } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';
import { authService, User } from './auth.service';
import { UserById } from './getUserById.service';

// GraphQL subscription for balance updates
export const BALANCE_UPDATED_SUBSCRIPTION = gql`
  subscription BalanceUpdated($userId: Int!) {
    balanceUpdated(userId: $userId) {
      id
      username
      name
      email
      phone
      credit
      isActive
      parentId
      comment
      lastLogin
      loginIp
      avatarUrl
      language
      timezone
      createdAt
      updatedAt
      totalActiveBonus
      wagerBonus
      dailyBonus
      hourlyBonus     
      balanceIn
      balanceOut
      profit
      hold
      holdPercentage
      role {
        id
        name
      }
      Balance {
        id
        amount
        credit
        onHold
      }
    }
  }
`;

// Types for balance subscription
export interface BalanceUpdatedData {
  id: number;
  username: string;
  name: string;
  email?: string;
  phone?: string;
  credit: number;
  isActive: boolean;
  parentId?: number;
  comment?: string;
  lastLogin?: string;
  loginIp?: string;
  avatarUrl?: string;
  language?: string;
  timezone?: string;
  createdAt: string;
  updatedAt: string;
  totalActiveBonus?: number;
  wagerBonus?: number;
  dailyBonus?: number;
  hourlyBonus?: number;
  balanceIn?: number;
  balanceOut?: number;
  profit?: number;
  hold?: number;
  holdPercentage?: number;
  role?: {
    id: number;
    name: string;
  };
  Balance?: {
    id: number;
    amount: number;
    credit: number;
    onHold: number;
  };
}

export interface BalanceSubscriptionCallbacks {
  onUpdate?: (data: BalanceUpdatedData) => void;
  onError?: (error: any) => void;
  onComplete?: () => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

// Balance Subscription service class
export class BalanceSubscriptionService {
  private static instance: BalanceSubscriptionService;
  private subscription: any = null;
  private isActive: boolean = false;
  private isActivePlayer: boolean = false;
  private callbacks: BalanceSubscriptionCallbacks = {};

  private constructor() {}

  public static getInstance(): BalanceSubscriptionService {
    if (!BalanceSubscriptionService.instance) {
      BalanceSubscriptionService.instance = new BalanceSubscriptionService();
    }
    return BalanceSubscriptionService.instance;
  }

  /**
   * Start balance subscription for the current user
   */
  async startSubscription(callbacks?: BalanceSubscriptionCallbacks, userId?: number): Promise<void> {
    try {
      // Get current user
      const user = authService.getStoredUser();
      if (!user?.id) {
        throw new Error('No authenticated user found');
      }

      // Stop existing subscription if running
      if (this.isActive) {
        await this.stopSubscription();
      }

      // Store callbacks
      this.callbacks = { ...callbacks };


      // Create subscription observable
      const subscriptionObservable = apolloClient.subscribe({
        query: BALANCE_UPDATED_SUBSCRIPTION,
        variables: { userId: userId || user.id },
      });

      // Subscribe to the observable
      this.subscription = subscriptionObservable.subscribe({
        next: (result) => {
          
          if (result.data?.balanceUpdated) {
            const updatedData = result.data.balanceUpdated as BalanceUpdatedData;
            
            // Update user in auth service
            const currentUser = authService.getStoredUser();
            if (currentUser) {
              const updatedUser: User | UserById = {
                ...currentUser,
                ...updatedData,
              };
              authService.setCurrentUser(updatedUser);
            }

            // Dispatch custom event for backward compatibility
            window.dispatchEvent(new CustomEvent('balanceUpdated', {
              detail: updatedData.Balance || updatedData
            }));

            // Call update callback
            if (this.callbacks.onUpdate) {
              this.callbacks.onUpdate(updatedData);
            }
          }
        },
        error: (error) => {
          console.error('❌ Balance subscription error:', error);
          this.isActive = false;
          
          // Call error callback
          if (this.callbacks.onError) {
            this.callbacks.onError(error);
          }
        },
        complete: () => {
          this.isActive = false;
          
          // Call complete callback
          if (this.callbacks.onComplete) {
            this.callbacks.onComplete();
          }
        }
      });

      this.isActive = true;

      // Call connect callback
      if (this.callbacks.onConnect) {
        this.callbacks.onConnect();
      }

    } catch (error) {
      console.error('❌ Error starting balance subscription:', error);
      throw error;
    }
  }
async startSubscriptionForPlayer(callbacks?: BalanceSubscriptionCallbacks, userId?: number): Promise<void> {
  try {
      // Get current user
      if (!userId) {
        throw new Error('No  user found');
      }

      // Stop existing subscription if running
      if (this.isActivePlayer) {
        await this.stopSubscriptionForPlayer();
      }

      // Store callbacks
      this.callbacks = { ...callbacks };


      // Create subscription observable
      const subscriptionObservable = apolloClient.subscribe({
        query: BALANCE_UPDATED_SUBSCRIPTION,
        variables: { userId: userId  },
      });

      // Subscribe to the observable
      this.subscription = subscriptionObservable.subscribe({
        next: (result) => {
          
          if (result.data?.balanceUpdated) {
            const updatedData = result.data.balanceUpdated as BalanceUpdatedData;
            
            // Update user in auth service

            // Dispatch custom event for backward compatibility
            window.dispatchEvent(new CustomEvent('balanceUpdated', {
              detail: updatedData.Balance || updatedData
            }));

            // Call update callback
            if (this.callbacks.onUpdate) {
              this.callbacks.onUpdate(updatedData);
            }
          }
        },
        error: (error) => {
          console.error('❌ Balance subscription error:', error);
          this.isActivePlayer = false;
          
          // Call error callback
          if (this.callbacks.onError) {
            this.callbacks.onError(error);
          }
        },
        complete: () => {
          this.isActivePlayer = false;
          
          // Call complete callback
          if (this.callbacks.onComplete) {
            this.callbacks.onComplete();
          }
        }
      });

      this.isActivePlayer = true;

      // Call connect callback
      if (this.callbacks.onConnect) {
        this.callbacks.onConnect();
      }

    } catch (error) {
      console.error('❌ Error starting balance subscription:', error);
      throw error;
    }
  }
  /**
   * Stop the active balance subscription
   */
  async stopSubscription(): Promise<void> {
    try {
      if (this.subscription) {
        this.subscription.unsubscribe();
        this.subscription = null;
      }
      
      this.isActive = false;
      this.callbacks = {};
      
      // Call disconnect callback
      if (this.callbacks.onDisconnect) {
        this.callbacks.onDisconnect();
      }

    } catch (error) {
      console.error('❌ Error stopping balance subscription:', error);
      throw error;
    }
  }
  async stopSubscriptionForPlayer(): Promise<void> {
    try {
      if (this.subscription) {
        this.subscription.unsubscribe();
        this.subscription = null;
      }
      
      this.isActivePlayer = false;
      this.callbacks = {};
      
      // Call disconnect callback
      if (this.callbacks.onDisconnect) {
        this.callbacks.onDisconnect();
      }

    } catch (error) {
      console.error('❌ Error stopping balance subscription:', error);
      throw error;
    }
  }

  /**
   * Check if subscription is currently active
   */
  isSubscriptionActive(): boolean {
    return this.isActive;
  }

  /**
   * Restart subscription (useful for reconnection)
   */
  async restartSubscription(userId?: number): Promise<void> {
    console.log('🔄 Restarting balance subscription...');
    const currentCallbacks = { ...this.callbacks };
    await this.stopSubscription();
    await this.startSubscription(currentCallbacks, userId);
  }

  /**
   * Update subscription callbacks
   */
  updateCallbacks(callbacks: BalanceSubscriptionCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }
}

// Export singleton instance
export const balanceSubscriptionService = BalanceSubscriptionService.getInstance();

// Convenience functions following your project pattern
export async function startBalanceSubscription(callbacks?: BalanceSubscriptionCallbacks, userId?: number): Promise<void> {
  return balanceSubscriptionService.startSubscription(callbacks, userId);
}
export async function startBalanceSubscriptionForPlayer(callbacks?: BalanceSubscriptionCallbacks, userId?: number): Promise<void> {
  return balanceSubscriptionService.startSubscriptionForPlayer(callbacks, userId);
}

export async function stopBalanceSubscription(): Promise<void> {
  return balanceSubscriptionService.stopSubscription();
}

export function isBalanceSubscriptionActive(): boolean {
  return balanceSubscriptionService.isSubscriptionActive();
}

export async function restartBalanceSubscription(): Promise<void> {
  return balanceSubscriptionService.restartSubscription();
}
